// src/pages/seller/SellerMessagesPage.jsx
import React, { useState, useEffect } from 'react';
import { MessageCircle, Clock, User } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { useNavigate } from 'react-router-dom';
import { db } from '../../utils/firebase';
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  getDoc
} from 'firebase/firestore';

const SellerMessagesPage = () => {
  const { user } = useApp();
  const navigate = useNavigate();
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [buyerMap, setBuyerMap] = useState({});

  useEffect(() => {
    console.log('SellerMessagesPage - User data:', user);
    console.log('SellerMessagesPage - User type:', user?.userType);
    console.log('SellerMessagesPage - User UID:', user?.uid);
    
    if (!user?.uid) {
      setLoading(false);
      setError('Please log in to access this page.');
      // 로그인하지 않은 경우 홈페이지로 리다이렉트
      setTimeout(() => {
        navigate('/');
      }, 2000);
      return;
    }
    
    if (user.userType !== 'seller') {
      setLoading(false);
      setError('Access denied. This page is only for sellers.');
      // seller가 아닌 경우 홈페이지로 리다이렉트
      setTimeout(() => {
        navigate('/');
      }, 2000);
      return;
    }

    const fetchChats = async () => {
      try {
        const q = query(collection(db, 'messages'), where('sellerId', '==', user.uid));
        const unsubscribe = onSnapshot(q, async (snapshot) => {
          const chatList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

          const buyerIds = [...new Set(chatList.map(c => c.buyerId).filter(Boolean))];
          const buyerData = {};
          await Promise.all(
            buyerIds.map(async (id) => {
              const buyerDoc = await getDoc(doc(db, 'users', id));
              if (buyerDoc.exists()) {
                buyerData[id] = buyerDoc.data();
              }
            })
          );
          setBuyerMap(buyerData);

          // 마지막 메시지 가져오기 (옵션: lastMessage 필드 사용)
          setChats(chatList.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)));
          setLoading(false);
        }, (err) => {
          setError('Failed to load messages.');
          setLoading(false);
          console.error(err);
        });

        return () => unsubscribe();
      } catch (err) {
        setError('Unexpected error.');
        setLoading(false);
        console.error(err);
      }
    };

    fetchChats();
  }, [user?.uid, user?.userType]);

  const handleChatClick = (chatId) => {
    navigate(`/seller/chat/${chatId}`);
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto py-12 text-center">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent"></div>
        <p className="mt-4 text-gray-600">Loading your messages...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto py-12 text-center">
      <p className="text-red-600">{error}</p>
    </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <h1 className="text-2xl font-medium text-gray-900 mb-6">Customer Messages</h1>
      {chats.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-8 text-center">
          <MessageCircle className="mx-auto text-gray-400 mb-4" size={48} />
          <p className="text-gray-600">No messages yet.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {chats.map((chat) => {
            const buyer = buyerMap[chat.buyerId] || {};
            return (
              <div
                key={chat.id}
                onClick={() => handleChatClick(chat.id)}
                className="border border-gray-200 rounded-lg p-4 bg-white hover:bg-gray-50 cursor-pointer transition-colors"
              >
                <div className="flex items-start gap-3">
                  <div className="bg-gray-200 rounded-full w-10 h-10 flex items-center justify-center">
                    <User size={18} className="text-gray-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-start">
                      <h3 className="font-semibold text-gray-900 truncate">
                        {buyer.displayName || 'Anonymous Buyer'}
                      </h3>
                      {chat.unreadCount > 0 && (
                        <span className="bg-blue-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                          {chat.unreadCount}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 truncate mt-1">
                      {chat.lastMessage || <span className="text-gray-400">No messages yet</span>}
                    </p>
                    <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                      <Clock size={12} />
                      <span>{chat.createdAt?.toDate().toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SellerMessagesPage;